import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { PREDEFINED_ACTIVITIES } from '../../../constants/predefinedActivities';
import './ActivityNameCombobox.scss';

function ActivityNameCombobox({ value, onChange, placeholder, className }) {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(PREDEFINED_ACTIVITIES);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);

  // Filter options based on input value
  useEffect(() => {
    if (!value) {
      setFilteredOptions(PREDEFINED_ACTIVITIES);
    } else {
      const filtered = PREDEFINED_ACTIVITIES.filter((activity) => activity.title.toLowerCase().includes(value.toLowerCase()));
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleOptionClick = (activity) => {
    onChange(activity.title);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionClick(filteredOptions[highlightedIndex]);
        } else if (value) {
          setIsOpen(false);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;

      default:
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [highlightedIndex]);

  return (
    <div ref={containerRef} className={`activity-combobox ${className || ''} ${isOpen ? 'activity-combobox--open' : ''}`}>
      <div className="activity-combobox__input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="activity-combobox__input"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        <button type="button" className="activity-combobox__toggle" onClick={() => setIsOpen(!isOpen)} tabIndex={-1} aria-label="Toggle options">
          <span className={`activity-combobox__arrow ${isOpen ? 'activity-combobox__arrow--up' : ''}`}>▼</span>
        </button>
      </div>

      {isOpen && (
        <div className="activity-combobox__dropdown">
          <ul ref={listRef} className="activity-combobox__list" role="listbox">
            {filteredOptions.map((activity, index) => (
              <li
                key={activity.seedName}
                className={`activity-combobox__option ${index === highlightedIndex ? 'activity-combobox__option--highlighted' : ''}`}
                onClick={() => handleOptionClick(activity)}
                role="option"
                aria-selected={value === activity.title}
              >
                {activity.title}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

ActivityNameCombobox.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
};

ActivityNameCombobox.defaultProps = {
  value: '',
  placeholder: 'Select or enter activity name...',
  className: '',
};

export default ActivityNameCombobox;
